import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import authApi from "../../../service/IdApi";
import PageLoadError from "../../../components/PageLoadError";
import PageHeading from "../../../components/PageHeading";
import PageLoading from "../../../components/PageLoading";
import { Chart } from "primereact/chart";
import { Dropdown } from 'primereact/dropdown';

const ServerMetrics = () => {
    const [range, setRange] = useState("3hours");
    const [chartDataMemory, setChartDataMemory] = useState(null);
    const [chartDataCpu, setChartDataCpu] = useState(null);
    const [chartDataAvailability, setChartDataAvailability] = useState(null);
    const [chartDataGcCount, setChartDataGcCount] = useState(null);
    const [chartDataGcTime, setChartDataGcTime] = useState(null);
    const [chartDataDbConnections, setChartDataDbConnections] = useState(null);
    const [chartDataDbResponseTime, setChartDataDbResponseTime] = useState(null);
    const [chartDataThreadPoolUsage, setChartDataThreadPoolUsage] = useState(null);
    const [chartDataThreadPoolQueue, setChartDataThreadPoolQueue] = useState(null);
    const [chartDataDiskSpace, setChartDataDiskSpace] = useState(null);
    const [chartDataLogFileSize, setChartDataLogFileSize] = useState(null);
    const [chartOptions, setChartOptions] = useState({});
    const [chartOptionsPercentage, setChartOptionsPercentage] = useState({});
    const [chartOptionsMinZero, setChartOptionsMinZero] = useState({});

    const rangeOptions = [
        { value: '30minutes', label: 'Last 30 Minutes' },
        { value: '3hours', label: 'Last 3 Hours' },
        { value: '24hours', label: 'Last 24 Hours' },
        { value: '3days', label: 'Last 3 Days' },
        { value: '7days', label: 'Last 7 Days' }
    ];

    const getDateRange = (rangeValue) => {
        const now = new Date();
        let startDate;
        switch (rangeValue) {
            case '30minutes':
                startDate = new Date(now.getTime() - 30 * 60 * 1000);
                break;
            case '3hours':
                startDate = new Date(now.getTime() - 3 * 60 * 60 * 1000);
                break;
            case '24hours':
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                break;
            case '3days':
                startDate = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
                break;
            case '7days':
                startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                break;
            default:
                startDate = new Date(now.getTime() - 3 * 60 * 60 * 1000);
        }
        return { startDate: startDate.toISOString(), endDate: now.toISOString() };
    };

    const formatTimestamp = (timestamp, rangeValue) => {
        const date = new Date(timestamp);
        switch (rangeValue) {
            case '30minutes':
            case '3hours':
            case '24hours':
                return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
            case '3days':
            case '7days':
                return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit' });
            default:
                return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        }
    };

    const { isLoading, isFetching, isError, data, refetch } = useQuery(
        ["ServerMetrics", range],
        async () => {
            const { startDate, endDate } = getDateRange(range);
            const result = await authApi.get("admin/healthcheck-metrics/trends", {
                params: { startDate, endDate }
            });
            return result.data.data;
        },
        {
            refetchInterval: 300000, // Refetch every 5 minutes
            keepPreviousData: true, // Keep previous data while fetching new data to prevent scroll position loss
        }
    );

    useEffect(() => {
        if (data && data.length > 0) {
            const labels = data.map(item => formatTimestamp(item.timestamp, range));
            
            // System Memory Percentage Chart
            const authMemoryData = data.map(item => 
                item.authServer ? item.authServer.systemMemoryPercentage : null
            );
            const providerMemoryData = data.map(item => 
                item.providerServer ? item.providerServer.systemMemoryPercentage : null
            );
            
            setChartDataMemory({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authMemoryData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerMemoryData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // System CPU Percentage Chart
            const authCpuData = data.map(item => 
                item.authServer ? item.authServer.systemCpuPercentage : null
            );
            const providerCpuData = data.map(item => 
                item.providerServer ? item.providerServer.systemCpuPercentage : null
            );
            
            setChartDataCpu({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authCpuData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerCpuData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Website Availability Chart
            // Collect all unique URLs
            const urlSet = new Set();
            data.forEach(item => {
                if (item.webRequests) {
                    item.webRequests.forEach(wr => {
                        if (wr.url) urlSet.add(wr.url);
                    });
                }
            });
            const urls = Array.from(urlSet);
            
            const colors = [
                { borderColor: '#007BFF', backgroundColor: 'rgba(0, 123, 255, 0.1)' },
                { borderColor: '#4CAF50', backgroundColor: 'rgba(76, 175, 80, 0.1)' },
                { borderColor: '#F7C223', backgroundColor: 'rgba(247, 194, 35, 0.1)' },
                { borderColor: '#1B9A59', backgroundColor: 'rgba(27, 154, 89, 0.1)' },
                { borderColor: '#DE3E35', backgroundColor: 'rgba(222, 62, 53, 0.1)' },
                { borderColor: '#9c1c93', backgroundColor: 'rgba(156, 28, 147, 0.1)' }
            ];

            const availabilityDatasets = urls.map((url, index) => {
                const color = colors[index % colors.length];
                return {
                    label: url,
                    data: data.map(item => {
                        if (item.webRequests) {
                            const wr = item.webRequests.find(w => w.url === url);
                            return wr ? (wr.available ? 1 : 0) : null;
                        }
                        return null;
                    }),
                    borderColor: color.borderColor,
                    backgroundColor: color.backgroundColor,
                    tension: 0.4
                };
            });

            setChartDataAvailability({
                labels: labels,
                datasets: availabilityDatasets
            });

            // GC Count Chart - Calculate deltas (change between measurements)
            const authGcCountData = data.map((item, index) => {
                if (!item.authServer || item.authServer.gcCount === null || item.authServer.gcCount === undefined) {
                    return null;
                }
                if (index === 0) {
                    return 0; // First data point has no delta
                }
                const prevItem = data[index - 1];
                if (!prevItem.authServer || prevItem.authServer.gcCount === null || prevItem.authServer.gcCount === undefined) {
                    return null;
                }
                const delta = item.authServer.gcCount - prevItem.authServer.gcCount;
                return delta >= 0 ? delta : null; // Only show positive deltas (shouldn't go backwards)
            });
            
            const providerGcCountData = data.map((item, index) => {
                if (!item.providerServer || item.providerServer.gcCount === null || item.providerServer.gcCount === undefined) {
                    return null;
                }
                if (index === 0) {
                    return 0; // First data point has no delta
                }
                const prevItem = data[index - 1];
                if (!prevItem.providerServer || prevItem.providerServer.gcCount === null || prevItem.providerServer.gcCount === undefined) {
                    return null;
                }
                const delta = item.providerServer.gcCount - prevItem.providerServer.gcCount;
                return delta >= 0 ? delta : null; // Only show positive deltas (shouldn't go backwards)
            });
            
            setChartDataGcCount({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authGcCountData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerGcCountData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // GC Time Chart - Calculate deltas (change between measurements) in milliseconds
            const authGcTimeData = data.map((item, index) => {
                if (!item.authServer || item.authServer.gcTime === null || item.authServer.gcTime === undefined) {
                    return null;
                }
                if (index === 0) {
                    return 0; // First data point has no delta
                }
                const prevItem = data[index - 1];
                if (!prevItem.authServer || prevItem.authServer.gcTime === null || prevItem.authServer.gcTime === undefined) {
                    return null;
                }
                const delta = item.authServer.gcTime - prevItem.authServer.gcTime;
                return delta >= 0 ? delta : null; // Only show positive deltas (shouldn't go backwards)
            });
            
            const providerGcTimeData = data.map((item, index) => {
                if (!item.providerServer || item.providerServer.gcTime === null || item.providerServer.gcTime === undefined) {
                    return null;
                }
                if (index === 0) {
                    return 0; // First data point has no delta
                }
                const prevItem = data[index - 1];
                if (!prevItem.providerServer || prevItem.providerServer.gcTime === null || prevItem.providerServer.gcTime === undefined) {
                    return null;
                }
                const delta = item.providerServer.gcTime - prevItem.providerServer.gcTime;
                return delta >= 0 ? delta : null; // Only show positive deltas (shouldn't go backwards)
            });
            
            setChartDataGcTime({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authGcTimeData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerGcTimeData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Database Available Connections Chart
            const authDbConnectionsData = data.map(item => 
                item.authServer ? item.authServer.databaseAvailableConnections : null
            );
            const providerDbConnectionsData = data.map(item => 
                item.providerServer ? item.providerServer.databaseAvailableConnections : null
            );
            
            setChartDataDbConnections({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authDbConnectionsData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerDbConnectionsData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Database Response Time Chart
            const authDbResponseTimeData = data.map(item => 
                item.authServer ? item.authServer.databaseResponseTime : null
            );
            const providerDbResponseTimeData = data.map(item => 
                item.providerServer ? item.providerServer.databaseResponseTime : null
            );
            
            setChartDataDbResponseTime({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authDbResponseTimeData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerDbResponseTimeData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Thread Pool Usage Percentage Chart (Active Threads / Pool Size * 100)
            const authThreadPoolUsageData = data.map(item => {
                if (!item.authServer || item.authServer.threadPoolSize === null || item.authServer.threadPoolSize === undefined || item.authServer.threadPoolSize === 0) {
                    return null;
                }
                if (item.authServer.threadPoolActiveThreads === null || item.authServer.threadPoolActiveThreads === undefined) {
                    return null;
                }
                return (item.authServer.threadPoolActiveThreads / item.authServer.threadPoolSize) * 100;
            });
            
            const providerThreadPoolUsageData = data.map(item => {
                if (!item.providerServer || item.providerServer.threadPoolSize === null || item.providerServer.threadPoolSize === undefined || item.providerServer.threadPoolSize === 0) {
                    return null;
                }
                if (item.providerServer.threadPoolActiveThreads === null || item.providerServer.threadPoolActiveThreads === undefined) {
                    return null;
                }
                return (item.providerServer.threadPoolActiveThreads / item.providerServer.threadPoolSize) * 100;
            });
            
            setChartDataThreadPoolUsage({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authThreadPoolUsageData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerThreadPoolUsageData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Thread Pool Request Queue Chart
            const authRequestQueueData = data.map(item => 
                item.authServer ? item.authServer.threadPoolRequestQueue : null
            );
            const providerRequestQueueData = data.map(item => 
                item.providerServer ? item.providerServer.threadPoolRequestQueue : null
            );
            
            setChartDataThreadPoolQueue({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authRequestQueueData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerRequestQueueData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Disk Space Usage Percentage Chart
            const authDiskPercentageData = data.map(item => 
                item.authServer ? item.authServer.diskUsagePercentage : null
            );
            const providerDiskPercentageData = data.map(item => 
                item.providerServer ? item.providerServer.diskUsagePercentage : null
            );
            
            setChartDataDiskSpace({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authDiskPercentageData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerDiskPercentageData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });

            // Disk Space Chart (convert bytes to MB)
            const bytesToMB = (bytes) => bytes / (1024 * 1024);
            
            // Log File Size Chart
            const authLogFileSizeData = data.map(item => 
                item.authServer ? bytesToMB(item.authServer.logFileSize) : null
            );
            const providerLogFileSizeData = data.map(item => 
                item.providerServer ? bytesToMB(item.providerServer.logFileSize) : null
            );
            
            setChartDataLogFileSize({
                labels: labels,
                datasets: [
                    {
                        label: 'Auth Server',
                        data: authLogFileSizeData,
                        borderColor: '#007BFF',
                        backgroundColor: 'rgba(0, 123, 255, 0.1)',
                        tension: 0.4
                    },
                    {
                        label: 'Provider Server',
                        data: providerLogFileSizeData,
                        borderColor: '#4CAF50',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        tension: 0.4
                    }
                ]
            });
        }
    }, [data, range]);

    useEffect(() => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color');
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border');
        const options = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                },
                y: {
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };
        
        // Options for percentage charts with fixed Y-axis (0-100)
        const optionsPercentage = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                },
                y: {
                    min: 0,
                    max: 100,
                    ticks: {
                        color: textColorSecondary
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };
        
        // Options for charts that should start at 0 (non-negative values) with whole numbers only
        const optionsMinZero = {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {
                    labels: {
                        color: textColor
                    }
                }
            },
            scales: {
                x: {
                    ticks: {
                        color: textColorSecondary,
                        font: {
                            weight: 500
                        }
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                },
                y: {
                    min: 0,
                    suggestedMax: 20,
                    ticks: {
                        color: textColorSecondary,
                        stepSize: 1,
                        callback: function(value) {
                            if (Number.isInteger(value)) {
                                return value;
                            }
                            return '';
                        }
                    },
                    grid: {
                        color: surfaceBorder,
                        drawBorder: false
                    }
                }
            }
        };
        
        setChartOptions(options);
        setChartOptionsPercentage(optionsPercentage);
        setChartOptionsMinZero(optionsMinZero);
    }, []);

    const pageHeader = (
        <PageHeading pageTitle="Admin - Server Metrics">
            <div className="block">
            </div>
        </PageHeading>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <PageLoadError />
            </div>
        );
    }

    if (isLoading) {
        return (
            <div>
                {pageHeader}
                <PageLoading />
            </div>
        );
    }

    return (
        <div>
            {pageHeader}
            <div className="layout-content">
                <div className="card pt-3 sm:pt-0">
                    <div className="flex pt-3 w-full">
                        <div className="flex flex-row text-lg align-items-center">
                            Server health metrics trends
                        </div>
                        <div className="flex flex-row flex-grow-1 justify-content-end">
                            <Dropdown 
                                value={range} 
                                onChange={(e) => {
                                    setRange(e.value);
                                    refetch();
                                }} 
                                options={rangeOptions} 
                                optionLabel="label" 
                                optionValue="value" 
                                placeholder="Select time range" 
                            />
                        </div>
                    </div>
                    
                    <div className="grid mt-4">
                        {chartDataMemory && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">System Memory Percentage</h3>
                                    <Chart type="line" data={chartDataMemory} options={chartOptionsPercentage} />
                                </div>
                            </div>
                        )}

                        {chartDataCpu && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">System CPU Percentage</h3>
                                    <Chart type="line" data={chartDataCpu} options={chartOptionsPercentage} />
                                </div>
                            </div>
                        )}

                        {chartDataAvailability && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Website Availability</h3>
                                    <Chart type="line" data={chartDataAvailability} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {chartDataGcCount && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Garbage Collection Count (Delta)</h3>
                                    <Chart type="line" data={chartDataGcCount} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {chartDataGcTime && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Garbage Collection Time Delta (ms)</h3>
                                    <Chart type="line" data={chartDataGcTime} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {chartDataDbConnections && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Database Available Connections</h3>
                                    <Chart type="line" data={chartDataDbConnections} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {chartDataDbResponseTime && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Database Response Time (ms)</h3>
                                    <Chart type="line" data={chartDataDbResponseTime} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {chartDataThreadPoolUsage && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Thread Pool Usage Percentage</h3>
                                    <Chart type="line" data={chartDataThreadPoolUsage} options={chartOptionsPercentage} />
                                </div>
                            </div>
                        )}

                        {chartDataThreadPoolQueue && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Thread Pool Request Queue</h3>
                                    <Chart type="line" data={chartDataThreadPoolQueue} options={chartOptionsMinZero} />
                                </div>
                            </div>
                        )}

                        {chartDataDiskSpace && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Disk Usage Percentage</h3>
                                    <Chart type="line" data={chartDataDiskSpace} options={chartOptionsPercentage} />
                                </div>
                            </div>
                        )}

                        {chartDataLogFileSize && (
                            <div className="col-12 md:col-6 lg:col-4">
                                <div className="card">
                                    <h3 className="text-xl font-bold mb-3">Log File Size (MB)</h3>
                                    <Chart type="line" data={chartDataLogFileSize} options={chartOptions} />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServerMetrics;

